import React, { useState, useEffect, useMemo } from 'react';
import { fetchUsers, bulkActionUsers, deleteUser } from '../api/userService';
import { getStoredUser } from '../utils/authStorage';
import EditUserModal from './EditUserModal';
import './AllUsers.css';

const AllUsers = () => {
  const currentUser = getStoredUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkRole, setBulkRole] = useState('user');

  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    search: '',
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  useEffect(() => {
    loadUsers();
  }, [filters.role, filters.status, filters.search, pagination.page, pagination.limit]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetchUsers({
        page: pagination.page,
        limit: pagination.limit,
        role: filters.role,
        status: filters.status,
        search: filters.search,
        sortBy: 'username',
        sortOrder: 'asc',
      });
      setUsers(response.users || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
      }));
      setError('');
    } catch (err) {
      console.error('Failed to load users', err);
      setError(err.response?.data?.message || 'Unable to load users. Please try again.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((user) => user._id || user.id));
    }
    setSelectAll(!selectAll);
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.length === 0) return;

    try {
      if (bulkAction === 'delete') {
        // Delete users one by one (or implement bulk delete in backend)
        for (const userId of selectedUsers) {
          await deleteUser(userId);
        }
      } else if (bulkAction === 'change-role') {
        await bulkActionUsers('change-role', selectedUsers, { role: bulkRole });
      }

      setSelectedUsers([]);
      setSelectAll(false);
      setBulkAction('');
      await loadUsers();
    } catch (err) {
      console.error('Bulk action failed', err);
      setError(err.response?.data?.message || 'Bulk action failed. Please try again.');
    }
  };

  const handleEditUser = (userId) => {
    setEditingUserId(userId);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingUserId(null);
  };

  const handleUserUpdated = () => {
    loadUsers();
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteUser(userId);
      setError('');
      await loadUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError(err.response?.data?.message || 'Failed to delete user. Please try again.');
    }
  };

  const roleCounts = useMemo(() => {
    const counts = { all: pagination.total };
    users.forEach((user) => {
      const role = user.role || 'user';
      counts[role] = (counts[role] || 0) + 1;
    });
    return counts;
  }, [users, pagination.total]);

  const roleLabels = {
    admin: 'Administrator',
    manager: 'Manager',
    technician: 'Developer',
    user: 'Viewer',
  };

  return (
    <div className="wp-all-users-container">
      <div className="wp-users-header">
        <div><h1 className="wp-page-title">Users</h1></div>
        <div><button
          type="button"
          className="wp-button-primary"
          onClick={() => {
            window.location.hash = 'add-user';
            window.location.reload();
          }}
        >
          Add User
        </button></div>
        
      </div>

      <div className="wp-users-filters">
        <ul className="wp-subsubsub">
          <li>
            <a
              href="#"
              className={filters.role === 'all' && filters.status === 'all' ? 'current' : ''}
              onClick={(e) => {
                e.preventDefault();
                setFilters((prev) => ({ ...prev, role: 'all', status: 'all' }));
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            >
              All <span className="count">({roleCounts.all || 0})</span>
            </a>
          </li>
          {Object.entries(roleLabels).map(([role, label]) => (
            <li key={role}>
              <a
                href="#"
                className={filters.role === role && filters.status === 'all' ? 'current' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  setFilters((prev) => ({ ...prev, role, status: 'all' }));
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
              >
                {label} <span className="count">({roleCounts[role] || 0})</span>
              </a>
            </li>
          ))}
        </ul>

        <div className="wp-users-search" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            className="wp-form-select"
            value={filters.status}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, status: e.target.value }));
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            style={{ width: 'auto', minWidth: '120px' }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <input
            type="search"
            placeholder="Search Users"
            className="wp-form-input"
            style={{ width: '200px' }}
            value={filters.search}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }));
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                loadUsers();
              }
            }}
          />
          <button 
            type="button" 
            className="wp-button-secondary"
            onClick={loadUsers}
          >
            Search Users
          </button>
        </div>
      </div>

      {error && (
        <div className="wp-notice wp-notice-error">
          <p>{error}</p>
        </div>
      )}

      <div className="wp-users-bulk-actions">
        <select
          className="wp-form-select"
          value={bulkAction}
          onChange={(e) => setBulkAction(e.target.value)}
          style={{ width: 'auto', marginRight: '5px' }}
        >
          <option value="">Bulk actions</option>
          <option value="change-role">Change role to...</option>
          <option value="delete">Delete</option>
        </select>
        {bulkAction === 'change-role' && (
          <select
            className="wp-form-select"
            value={bulkRole}
            onChange={(e) => setBulkRole(e.target.value)}
            style={{ width: 'auto', marginRight: '5px' }}
          >
            <option value="user">Viewer</option>
            <option value="technician">Developer</option>
            <option value="manager">Manager</option>
            <option value="admin">Administrator</option>
          </select>
        )}
        <button
          type="button"
          className="wp-button-secondary"
          onClick={handleBulkAction}
          disabled={!bulkAction || selectedUsers.length === 0}
        >
          Apply
        </button>
      </div>

      <div className="wp-list-table-wrapper">
        <table className="wp-list-table widefat fixed striped table-view-list">
        <thead>
          <tr>
            <td className="manage-column column-cb check-column">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
            </td>
            <th className="manage-column column-username column-primary sortable desc">
              <a href="#">
                <span>Username</span>
                <span className="sorting-indicator"></span>
              </a>
            </th>
            <th className="manage-column column-name">Name</th>
            <th className="manage-column column-email sortable desc">
              <a href="#">
                <span>Email</span>
                <span className="sorting-indicator"></span>
              </a>
            </th>
            <th className="manage-column column-role">Role</th>
            <th className="manage-column column-posts num">Posts</th>
            <th className="manage-column column-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                Loading users...
              </td>
            </tr>
          ) : users.length === 0 ? (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                No users found
              </td>
            </tr>
          ) : (
            users.map((user) => {
              const userId = user._id || user.id;
              const isSelected = selectedUsers.includes(userId);
              const avatarInitials = (user.fullName || user.username || 'U')
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);

              return (
                <tr key={userId} className={isSelected ? 'selected' : ''}>
                  <th scope="row" className="check-column">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectUser(userId)}
                    />
                  </th>
                  <td className="username column-username has-row-actions column-primary">
                    <strong>
                      <a 
                        href="#" 
                        className="wp-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handleEditUser(userId);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <span
                          className="wp-user-avatar"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: '#2271b1',
                            color: '#fff',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '8px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                          }}
                        >
                          {avatarInitials}
                        </span>
                        {user.username}
                      </a>
                    </strong>
                  </td>
                  <td className="name column-name">{user.fullName || '—'}</td>
                  <td className="email column-email">
                    <a href={`mailto:${user.email}`} className="wp-link">
                      {user.email}
                    </a>
                  </td>
                  <td className="role column-role">
                    {roleLabels[user.role] || user.role}
                  </td>
                  <td className="posts column-posts">
                    <a href="#" className="wp-link">
                      0
                    </a>
                  </td>
                  <td className="actions column-actions">
                    <div className="row-actions">
                      <span className="edit">
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleEditUser(userId);
                          }}
                        >
                          Edit
                        </a>
                      </span>
                      <span className="delete">
                        <a
                          href="#"
                          className="delete-user-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeleteUser(userId);
                          }}
                        >
                          Delete
                        </a>
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
        <tfoot>
          <tr>
            <td className="manage-column column-cb check-column">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
            </td>
            <th className="manage-column column-username column-primary sortable desc">
              <a href="#">
                <span>Username</span>
                <span className="sorting-indicator"></span>
              </a>
            </th>
            <th className="manage-column column-name">Name</th>
            <th className="manage-column column-email sortable desc">
              <a href="#">
                <span>Email</span>
                <span className="sorting-indicator"></span>
              </a>
            </th>
            <th className="manage-column column-role">Role</th>
            <th className="manage-column column-posts num">Posts</th>
            <th className="manage-column column-actions">Actions</th>
          </tr>
        </tfoot>
      </table>
      </div>

      <div className="wp-users-pagination">
        <span className="displaying-num">
          {pagination.total} {pagination.total === 1 ? 'item' : 'items'}
        </span>
      </div>

      <EditUserModal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        userId={editingUserId}
        onUserUpdated={handleUserUpdated}
      />
    </div>
  );
};

export default AllUsers;

